import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Mail, RotateCcw, XCircle, Clock, CheckCircle, AlertTriangle, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import api from '../../lib/api';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'En attente', icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  accepted: { label: 'Acceptée', icon: CheckCircle, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expired: { label: 'Expirée', icon: AlertTriangle, color: 'bg-red-50 text-red-600 border-red-200' },
  cancelled: { label: 'Annulée', icon: XCircle, color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const AdminInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadInvitations(); }, []);

  const loadInvitations = async () => {
    try {
      const res = await api.get('/partner-invitations');
      setInvitations(res.data || []);
    } catch (err) {
      console.error('Error loading invitations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.post(`/partner-invitations/${id}/cancel`);
      toast.success('Invitation annulée');
      await loadInvitations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleResend = async (id) => {
    try {
      const res = await api.post(`/partner-invitations/${id}/resend`);
      toast.success('Invitation renvoyée');
      if (res.data?.invite_url) {
        navigator.clipboard?.writeText(res.data.invite_url);
        toast.success('Lien copié dans le presse-papier');
      }
      await loadInvitations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const pendingCount = invitations.filter(i => i.status === 'pending').length;
  const filtered = statusFilter === 'all' ? invitations : invitations.filter(i => i.status === statusFilter);

  return (
    <DashboardLayout>
    <div className="space-y-6 max-w-4xl" data-testid="admin-invitations-page">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Invitations</h1>
        <p className="text-sm text-slate-500">
          {invitations.length} invitation{invitations.length > 1 ? 's' : ''} — {pendingCount} en attente
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'accepted', 'expired', 'cancelled'].map(s => (
          <Button
            key={s} size="sm"
            variant={statusFilter === s ? 'default' : 'outline'}
            onClick={() => setStatusFilter(s)}
            data-testid={`filter-${s}`}
          >
            {s === 'all' ? 'Toutes' : STATUS_CONFIG[s]?.label || s} ({s === 'all' ? invitations.length : invitations.filter(i => i.status === s).length})
          </Button>
        ))}
      </div>

      {/* Invitations list */}
      {loading ? (
        <p className="text-sm text-slate-400">Chargement...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-400">Aucune invitation{statusFilter !== 'all' ? ` ${STATUS_CONFIG[statusFilter]?.label?.toLowerCase() || ''}` : ''}</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(inv => {
            const st = STATUS_CONFIG[inv.status] || STATUS_CONFIG.pending;
            const StIcon = st.icon;
            return (
              <Card key={inv.id} className={inv.status === 'pending' ? 'border-amber-200' : ''} data-testid={`invitation-${inv.id}`}>
                <CardContent className="pt-4 pb-4 flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <span className="text-sm font-semibold text-slate-900">{inv.email}</span>
                      <Badge className={`text-[10px] border ${st.color}`}>
                        <StIcon className="h-3 w-3 mr-0.5" />{st.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {inv.partner_nom || 'Partner'} — {new Date(inv.created_at).toLocaleDateString('fr-FR')}
                      {inv.expires_at && inv.status === 'pending' && (
                        <span className="ml-2 text-amber-600">
                          Expire le {new Date(inv.expires_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {(inv.status === 'pending' || inv.status === 'expired') && (
                      <Button size="sm" variant="outline" onClick={() => handleResend(inv.id)} className="gap-1" data-testid={`resend-${inv.id}`}>
                        <RotateCcw className="h-3 w-3" /> Renvoyer
                      </Button>
                    )}
                    {inv.status === 'pending' && (
                      <Button size="sm" variant="outline" onClick={() => handleCancel(inv.id)} className="gap-1 text-red-600 border-red-200 hover:bg-red-50" data-testid={`cancel-${inv.id}`}>
                        <XCircle className="h-3 w-3" /> Annuler
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
    </DashboardLayout>
  );
};

export default AdminInvitations;
